import { useState, useEffect, useRef } from 'react'
import { Container, Col, Row, Badge } from 'reactstrap'
import { useSelector, useDispatch } from 'react-redux'
import { getAllAnalyzers } from '../../redux/slices'
import './Analyzers.css'
import ComponentBox from '../../components/ComponentBox/ComponentBox'
import { renderDateTime } from '../../utils/dateFormatter'
import 'devextreme/dist/css/dx.common.css'
import 'devextreme/dist/css/dx.light.css'
import ICSDataGrid from '../../components/ICSDataGrid/ICSDataGrid'
import { ICSModal } from '../../components'
// import RenderDataModal from '../Data/Modals/RenderViewModal/RenderViewModal'
import { createEntityAdapter } from '@reduxjs/toolkit'
import {
  removeAnalyzer,
  removeAnalyzerResults,
  setEntityError
} from '../../redux/slices'
import { DefaultState } from '../../types'
import HeaderBar from '../../components/HeaderBar/HeaderBar'
import { ICSDataCellDetailView } from '../../components/ICSDataCellDetailView/ICSDataCellDetailView'
import { capitalizeFirstLetter } from '../../utils/helpers'
import { ICSActionIcon } from '../../components'
import ICSActionButton from '../../components/ICS/ActionButton/ICSActionButton'
import ICSErrorCard from '../../components/ICSErrorCard/ICSErrorCard'
import { AppDispatch, NetworkRequestError } from '../../services/lib'
import AnalyzerFormWrapper from './AnalyzerGenerator/AnalyzerFormWrapper'
import { postIpaManual } from '../../services/endpoints/jobQueue'
import { showToast } from '../../utils/types'

const badgeColorMap = {
  status: {
    new: 'secondary',
    running: 'primary',
    complete: 'success',
    failed: 'danger'
  },
  dataSet: {
    static: 'dark',
    dynamic: 'warning'
  }
}

const LastStatusBadge = (data: { value: string }) => {
  return (
    <Badge color={badgeColorMap.status[data.value] || 'primary'} pill>
      {data.value && capitalizeFirstLetter(data.value)}
    </Badge>
  )
}

const DataSetBadge = (data: { value: string }) => {
  return (
    <Badge color="info" pill>
      {data.value && capitalizeFirstLetter(data.value)}
    </Badge>
  )
}

const Analyzers = () => {
  const dispatch: AppDispatch = useDispatch()
  const analyzersError = useSelector(
    (state: DefaultState) => state.analyzers.error
  )
  const currentUser = useSelector(
    (state: DefaultState) => state.session.data.user
  )
  const selectors = createEntityAdapter().getSelectors()
  const analyzersState = useSelector((state: DefaultState) => state.analyzers)
  const analyzersArray = selectors.selectAll(analyzersState)
  const [showGenerateModal, setShowGenerateModal] = useState<boolean>(false)
  const [showEditModal, setShowEditModal] = useState<boolean>(false)
  const [editAnalyzer, setEditAnalyzer] = useState(null)
  const interval: { current: NodeJS.Timeout | null } = useRef(null)

  const enqueueManualRun = async (
    analyzerId: string | number,
    analyzerName?: string
  ) => {
    dispatch(
      setEntityError({
        sliceName: 'analyzers',
        entityName: 'Analyzer',
        error: null
      })
    )

    try {
      await postIpaManual(String(analyzerId))
      const successMessage = analyzerName
        ? `Analyzer "${analyzerName}" run queued`
        : 'Analyzer run queued'
      showToast(successMessage, 'success', 2500)
      return true
    } catch (error) {
      const networkError = error as NetworkRequestError
      const errorMessage =
        networkError?.message || 'Failed to queue analyzer run.'

      dispatch(
        setEntityError({
          sliceName: 'analyzers',
          entityName: 'Analyzer',
          error: {
            code: networkError?.code || 'error',
            message: errorMessage,
            fields: networkError?.fields || []
          }
        })
      )
      showToast(errorMessage, 'error', 4000)
      return false
    }
  }

  useEffect(() => {
    const initialState = async () => {
      await dispatch(getAllAnalyzers())
    }

    if (showGenerateModal || showEditModal) {
      clearInterval(interval.current as NodeJS.Timeout)
    } else {
      interval.current = setInterval(async () => {
        // eslint-disable-next-line no-console
        initialState()
      }, 10000)
    }
  }, [dispatch, showGenerateModal, showEditModal])

  useEffect(() => {
    dispatch(getAllAnalyzers())
  }, [])

  const analyzerGenerateButtonClicked = () => {
    setShowGenerateModal(true)
  }
  const hideEditModal = () => {
    setShowEditModal(false)
    dispatch(
      setEntityError({
        sliceName: 'analyzers',
        entityName: 'Analyzer',
        error: null
      })
    )
  }
  const hideModal = () => {
    setShowGenerateModal(false)
    dispatch(
      setEntityError({
        sliceName: 'analyzers',
        entityName: 'Analyzer',
        error: null
      })
    )
  }
  const bypassHandler = (e) => {
    e.preventDefault()
  }
  const handleEdit = (_, data) => {
    if (data) {
      setEditAnalyzer(data)
      setShowEditModal(true)
    }
  }
  const handleRun = async (_, data) => {
    if (data) {
      const r = window.confirm(
        `Are you sure you want to run analyzer (${data.name})?`
      )
      if (r === true) {
        await enqueueManualRun(data.id, data.name)
      }
    }
  }
  const handleDelete = (_, data) => {
    if (data) {
      const r = window.confirm(
        `Are you sure you want to delete the following Analyzer: ${data.name}?`
      )
      if (r === true) {
        dispatch(removeAnalyzer(data.id))
      }
    }
  }
  const handleDeleteResults = (_, data) => {
    if (data) {
      const r = window.confirm(
        `Are you sure you want to delete ALL results currently assigned to Analyzer: ${data.name}`
      )
      if (r === true) {
        dispatch(removeAnalyzerResults(data.id))
      }
    }
  }
  const checkEnabled = (data: any) => {
    if (!data || !data.user || !data.user.id) return false
    const enabled =
      typeof data.last_status === 'string' && data.last_status === 'running'
        ? false
        : true
    return enabled
  }

  return (
    <>
      <Container fluid={true}>
        <>
          {showGenerateModal && (
            <ICSModal
              visible={showGenerateModal}
              onHiding={hideModal}
              title="Create New Analyzer"
              position="center"
              id="analyzerModal"
            >
              <AnalyzerFormWrapper
                isEdit={false}
                onSuccess={async (createdAnalyzer) => {
                  if (
                    createdAnalyzer &&
                    createdAnalyzer.type === 'manual' &&
                    window.confirm(
                      `Run newly created analyzer (${createdAnalyzer.name})?`
                    )
                  ) {
                    await enqueueManualRun(
                      createdAnalyzer.id,
                      createdAnalyzer.name
                    )
                  }
                  hideModal()
                }}
              />
            </ICSModal>
          )}
        </>
        <>
          {showEditModal && editAnalyzer && (
            <ICSModal
              visible={showEditModal}
              onHiding={hideEditModal}
              title="Edit Analyzer"
              position="center"
              id="analyzerEditModal"
            >
              <AnalyzerFormWrapper
                isEdit={true}
                selectedAnalyzer={editAnalyzer}
                onSuccess={async (updatedAnalyzer) => {
                  if (
                    updatedAnalyzer &&
                    updatedAnalyzer.type === 'manual' &&
                    window.confirm(
                      `Run updated analyzer (${updatedAnalyzer.name})?`
                    )
                  ) {
                    await enqueueManualRun(
                      updatedAnalyzer.id,
                      updatedAnalyzer.name
                    )
                  }
                  hideEditModal()
                }}
              />
            </ICSModal>
          )}
        </>
        <Row>
          <Col className="wivi-col">
            <HeaderBar
              origin="IPA"
              routes={[
                {
                  title: 'Analyzers'
                }
              ]}
              help={{
                title: 'Analyzers',
                text: (
                  <>
                    <p>
                      Analyzers are the primary component of IPA and consist of
                      many of the other components. Analyzers are easy to
                      generate and edit and belong to the User who created them
                      specifically. To create one, simply navigate to IPA &gt;
                      Analyzer in the nav bar, and click the “Generate” button
                      which will prompt you to provide the components of an
                      Analyzer.{' '}
                    </p>

                    <p>
                      Analyzer’s necessarily require a Name, a Run Type ( either
                      Dynamic or Static ), an Analysis Script, and a Dataset. An
                      Analyzer also optionally includes Configurations, and
                      Users with whom you’d wish to automatically share the
                      results of the analysis with upon completion. Multiple
                      Analysis Scripts, Datasets, Configurations, and Users may
                      be applied to a given Analyzer.
                    </p>

                    <p>
                      When an Analyzer has been created you will see it on the
                      grid on the Analyzer page. To run a static Analyzer simply
                      click the run icon on the left of the grid. This queues a
                      manual run request that the IPA Scanner picks up and
                      processes. Processing in this case means a container will
                      be launched with all the necessary information, then the
                      selected Analysis script will run with any optional
                      configurations on the selected data set(s), and the
                      results will be outputted to the Results page. If there
                      were any issues with the container or the Python script
                      during processing, you will see a DockerLog.txt file
                      appear in the results which contains the error message.{' '}
                    </p>

                    <p>
                      {' '}
                      To activate a Dynamic dataset you simply click the
                      “active” check mark on the grid. The check mark box will
                      be green when it is active. When a dynamic Dataset is
                      active it means all new uploaded collections will be
                      checked against the dataset criteria, and if they match,
                      the analysis will be run.{' '}
                    </p>

                    <p>
                      {' '}
                      You can edit or delete Analyzers, using the corresponding
                      grid buttons, as you see fit as all Analyzers are unique
                      to the person who created them.{' '}
                    </p>
                  </>
                )
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={{ size: 12 }} className="wivi-col">
            <ComponentBox>
              {analyzersError?.code === 'error' && !showGenerateModal ? (
                <ICSErrorCard
                  code={analyzersError.code}
                  message={analyzersError.message}
                  fields={analyzersError.fields}
                />
              ) : (
                <ICSDataGrid
                  dataSource={analyzersArray}
                  columnOptions={[
                    {
                      type: 'buttons',
                      caption: 'Action',
                      width: 110,
                      buttons: [
                        {
                          render: (e) => {
                            const { data } = e
                            return (
                              <ICSActionIcon
                                key={`action_edit_${data.id}`}
                                icon="edit"
                                title="Edit"
                                renderData={e}
                                onClick={
                                  checkEnabled(e.row.data)
                                    ? (e) => {
                                        handleEdit(e, data)
                                      }
                                    : bypassHandler
                                }
                                disabled={!checkEnabled(e.row.data)}
                              />
                            )
                          }
                        },
                        {
                          hint: 'run',
                          title: 'Run',
                          render: (e) => {
                            const { data } = e
                            if (data.type === 'ingress') return null
                            return (
                              <ICSActionIcon
                                key={`action_run_${data.id}`}
                                icon="runner"
                                title="Run"
                                renderData={e}
                                onClick={
                                  checkEnabled(e.row.data)
                                    ? (e) => {
                                        handleRun(e, data)
                                    }
                                  : bypassHandler
                                }
                                disabled={!checkEnabled(e.row.data)}
                              />
                            )
                          }
                        },
                        {
                          icon: 'deletetable',
                          hint: 'Delete All Results',
                          render: (e) => {
                            const { data } = e
                            if (data.type === 'ingress') return null
                            return (
                              <ICSActionIcon
                                key={`action_delete_results_${data.id}`}
                                icon="deletetable"
                                title="Delete All Results"
                                renderData={e}
                                onClick={(e) => {
                                  checkEnabled(data)
                                    ? handleDeleteResults(e, data)
                                    : bypassHandler(e)
                                }}
                                disabled={!checkEnabled(e.row.data)}
                              />
                            )
                          }
                        },
                        {
                          icon: 'trash',
                          hint: 'Delete',
                          render: (e) => {
                            const { data } = e
                            return (
                              <ICSActionIcon
                                key={`action_delete_${data.id}`}
                                icon="trash"
                                title="Delete"
                                renderData={e}
                                onClick={(e) => {
                                  checkEnabled(data)
                                    ? handleDelete(e, data)
                                    : bypassHandler(e)
                                }}
                                disabled={!checkEnabled(e.row.data)}
                              />
                            )
                          }
                        }
                      ]
                    },
                    { dataField: 'name', caption: 'Name' },
                    {
                      dataField: 'description',
                      caption: 'Description',
                      cssClass: 'td-dataset-description',
                      cellRender: (data) => {
                        return (
                          <ICSDataCellDetailView
                            {...data}
                            modalWidth="35vw"
                            modalHeight="20vh"
                          />
                        )
                      }
                    },
                    { dataField: 'type', caption: 'Type' },
                    {
                      dataField: 'script.name',
                      caption: 'Script Name',
                      cellRender: (col) => {
                        const scriptName = col.data.script.name
                        if (!scriptName) {
                          return (
                            <span style={{ color: 'red' }}>
                              Script is missing
                            </span>
                          )
                        }
                        return <span>{scriptName}</span>
                      }
                    },
                    {
                      dataField: 'configs[0].name',
                      caption: 'Configuration',
                      cellRender: (col) => {
                        const scriptName = col.data.script.name
                        if (!scriptName) {
                          return (
                            <span style={{ color: 'red' }}>
                              Config is missing
                            </span>
                          )
                        }
                        return <span>{col.data.configs[0].name}</span>
                      }
                    },
                    {
                      dataField: 'datasets[0].name',
                      caption: 'Data Sets',
                      cellRender: (col) => {
                        return (
                          <>
                            <DataSetBadge value={col.value} />
                            &nbsp;
                          </>
                        )
                      }
                    },
                    {
                      dataField: 'last_status',
                      caption: 'Last Status',
                      cellRender: (col) => {
                        const { last_status } = col.data
                        if (col.data.type === 'ingress') return null
                        return (
                          <>
                            <LastStatusBadge value={last_status} />
                            &nbsp;
                          </>
                        )
                      }
                    },
                    {
                      dataField: 'user.name',
                      caption: 'User'
                    },
                    {
                      dataField: 'created_at',
                      caption: 'Date Created',
                      cellRender: renderDateTime
                    },
                    {
                      dataField: 'updated_at',
                      caption: 'Last Updated',
                      cellRender: renderDateTime
                    }
                  ]}
                  options={{
                    buttons: [
                      <ICSActionButton
                        id="analyzerGenerate"
                        type="success"
                        width={65}
                        height={35}
                        stylingMode="contained"
                        onClick={analyzerGenerateButtonClicked}
                        text="Add"
                      >
                        <span className="glyphicons glyphicons-floppy-disk"></span>
                        <span className="glyphicons glyphicons-plus"></span>
                      </ICSActionButton>
                    ],
                    showScrollbar: 'always'
                  }}
                />
              )}
            </ComponentBox>
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default Analyzers
